import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { Tooltip, useMediaQuery } from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import Grid from "../Shared/Grid/Grid";
import Slider from "../Shared/Widgets/Slider/Slider";
import Awardee from "../Awardee/Awardees";
import AwardsHero from "./AwardsHero";
import UtaProfileLink from "../Shared/UtaProfileLink/UtaProfileLink";
import {
  useLazyGetAwardeeByIdQuery,
} from "../../services/rtkQueryServices/checkApiService";
import { useThemeMode } from "../../theme/ThemeContext";
import useAwardSearch from "./useAwardSearch";
import NAM from "../../assets/images/NAM.png";
import NAE from "../../assets/images/NAE.png";
import AAS from "../../assets/images/AAS.png";
import NAS from "../../assets/images/NAS.jpg";
import NAI from "../../assets/images/NAI.jpeg";
import UTA_MAVERICK from "../../assets/images/uta_mav_logo.png";
import "./AwardsSearch.scss";

const CURRENT_YEAR = new Date().getFullYear();
const clean = (v) => (typeof v === "string" ? v.replace(/\r/g, " ").trim() : v);
const externalHref = (url) =>
  !url ? null : /^https?:\/\//i.test(url) ? url : `https://${url}`;

// The unified results pane. All search/filter state lives in the URL (via the
// persistent SearchShell above us); here we just render the active tab's grid
// from the data the layout fetched, and write pagination/sort back to the URL.
export default function AwardsResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isUta } = useThemeMode();
  const { params, setParam, setParams, activeFacetCount } = useAwardSearch();
  const { awardsData, awardsLoading, peopleData, peopleLoading } =
    useOutletContext();

  const [openSlider, setOpenSlider] = useState(false);
  const [sliderTitle, setSliderTitle] = useState("");
  const [sliderUsername, setSliderUsername] = useState(null);
  const [fetchAwardee, { data: awardeeData }] = useLazyGetAwardeeByIdQuery();

  const isAwardees = params.type === "awardees";
  const isMobile = useMediaQuery("(max-width:600px)");

  // ── Shared pagination / sort writers (URL is the source of truth). Each is a
  // no-op when the incoming value already matches the URL, so the Grid echoing
  // its state back can't trigger redundant writes. ──
  const onPageChange = (value) => {
    if (value !== params.page) setParam("page", value);
  };
  const onPageSizeChange = (value) => {
    if (value !== params.size) setParams({ size: value, page: 1 });
  };
  const onSort = (columnName, order) => {
    if (!columnName) return; // ignore the Grid's mount-time sortBy(undefined)
    setParams(
      order === "asc" || order === "desc"
        ? { sort: columnName, dir: order, page: 1 }
        : { sort: "", dir: "", page: 1 }
    );
  };

  // ── Awards tab ──────────────────────────────────────────────
  const awardRows = (awardsData?.data || []).map((award) => ({
    awardName: award.Award,
    awardkind: award.Amount === "Unspecified" ? "Recognition" : award.Amount,
    category: clean(award.Category),
    level: clean(award.Level),
    organization: award.Organization,
    percentageWomen: award.PercentageWomen,
    mostRecentYear: award.mostRecentYear,
    dueDate: clean(award.Due_date_approximate),
    aauPrestigious: award["!AAU_HighlyPrestigious"] === "checked",
    utaAward: Number(award.UTA_faculty) === 1,
    id: award.pk_award,
    website: award.Website,
  }));

  const renderAwardName = ({ value, data }) => {
    const href = externalHref(data?.website);
    return (
      <div className="award-name-cell">
        <span className="award-name-text" title={value}>
          {value}
        </span>
        {data?.aauPrestigious && (
          <Tooltip title="Honorific">
            <WorkspacePremiumIcon className="award-aau-ribbon" fontSize="small" />
          </Tooltip>
        )}
        {isUta && data?.utaAward && (
          <Tooltip title="Awarded to UTA faculty">
            <img className="award-uta-maverick" src={UTA_MAVERICK} alt="UTA" />
          </Tooltip>
        )}
        {href && (
          <a
            className="award-website-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title="Award website"
            onClick={(e) => e.stopPropagation()}
          >
            ↗
          </a>
        )}
      </div>
    );
  };

  const renderWomen = ({ value }) =>
    value === null || value === undefined || value === "" ? (
      <span className="award-muted">-</span>
    ) : (
      `${value}%`
    );

  const renderActivity = ({ data }) => {
    const year = data?.mostRecentYear;
    if (clean(data?.dueDate) === "Discontinued")
      return <span className="award-activity-discontinued">Discontinued</span>;
    if (year === null || year === undefined || year === "")
      return <span className="award-muted">-</span>;
    if (Number(year) >= CURRENT_YEAR - 1)
      return (
        <span className="award-activity-active" title={`Most recent: ${year}`}>
          Active
        </span>
      );
    return <span className="award-activity-old">{year}</span>;
  };

  const awardColumnDefs = [
    { headerName: "Award", field: "awardName", style: { minWidth: "240px" }, sortable: true, cellRenderer: renderAwardName },
    { headerName: "Organization", field: "organization", style: { minWidth: "160px", maxWidth: "240px" }, sortable: true },
    { headerName: "Category", field: "category", style: { minWidth: "110px" }, sortable: true },
    { headerName: "Level", field: "level", style: { minWidth: "100px" }, sortable: true },
    { headerName: "Kind", field: "awardkind", style: { minWidth: "100px", maxWidth: "180px" }, sortable: true, cellClass: "wrap-cell" },
    { headerName: "% Women", field: "percentageWomen", style: { minWidth: "90px" }, align: "center", sortable: true, cellRenderer: renderWomen },
    { headerName: "Activity", field: "mostRecentYear", style: { minWidth: "110px" }, sortable: true, cellRenderer: renderActivity },
  ];

  const handleAwardClicked = (row) => {
    if (row.id && row.awardName)
      // Carry the current search onto the detail URL so the persistent shell
      // stays in sync and snapping back preserves the active filters.
      navigate(
        { pathname: `/award/${row.id}`, search: location.search },
        { state: { currentAwardData: row } }
      );
  };

  // ── People tab ──────────────────────────────────────────────
  const peopleRows = (peopleData?.data || []).map((awardee) => ({
    fullName: awardee.FullName,
    nam: !!Number(awardee.NAM),
    nae: !!Number(awardee.NAE),
    nas: !!Number(awardee.NAS),
    nai: !!Number(awardee.NAI),
    aas: !!Number(awardee.AAS),
    academyCount: awardee.academyCount,
    lastYear: awardee.lastYear,
    isUTA: awardee.uta,
    utaUsername: awardee.utaUsername,
    id: awardee.RaiseProjectID,
    noOfAwards: awardee.noOfAwards,
  }));

  const renderFullName = ({ value, data }) => (
    <UtaProfileLink username={data?.utaUsername}>
      <span className={isUta && data?.isUTA === 1 ? "org-bg p-2" : ""}>{value}</span>
    </UtaProfileLink>
  );

  // Academy memberships as a compact icon row (tooltip names each academy).
  const ACADEMIES = [
    ["nam", NAM, "NAM", "National Academy of Medicine"],
    ["nae", NAE, "NAE", "National Academy of Engineering"],
    ["nas", NAS, "NAS", "National Academy of Sciences"],
    ["nai", NAI, "NAI", "National Academy of Inventors"],
    ["aas", AAS, "AAS", "American Academy of Arts & Sciences"],
  ];
  const renderAcademies = ({ data }) => {
    const badges = ACADEMIES.filter(([k]) => data?.[k]);
    if (!badges.length) return <span className="award-muted">-</span>;
    return (
      <div className="awardee-academies">
        {badges.map(([k, src, alt, title]) => (
          <img key={k} className="award-image" src={src} alt={alt} title={title} />
        ))}
      </div>
    );
  };

  const renderYear = ({ value }) =>
    value ? value : <span className="award-muted">-</span>;

  const peopleColumnDefs = [
    { headerName: "Awardee", field: "fullName", style: { minWidth: "220px" }, sortable: true, cellRenderer: renderFullName },
    { headerName: "# Awards", field: "noOfAwards", style: { minWidth: "90px" }, align: "center", sortable: true },
    { headerName: "Academies", field: "academyCount", style: { minWidth: "140px" }, sortable: true, cellRenderer: renderAcademies },
    { headerName: "Most recent", field: "lastYear", style: { minWidth: "110px" }, align: "center", sortable: true, cellRenderer: renderYear },
  ];

  const handlePersonClicked = (row) => {
    if (row.id) fetchAwardee(row.id);
    setSliderTitle(row?.fullName);
    setSliderUsername(row?.utaUsername || null);
    setOpenSlider(true);
  };

  const totalRecords = isAwardees
    ? peopleData?.totalRecords || 0
    : awardsData?.totalRecords || 0;

  // Snap an out-of-range page (deep-link or a shrunk result set) back to the
  // last real page instead of showing an empty grid on a valid-looking URL.
  useEffect(() => {
    if (!totalRecords) return;
    const lastPage = Math.max(1, Math.ceil(totalRecords / params.size));
    if (params.page > lastPage) setParam("page", lastPage, { replace: true });
  }, [totalRecords, params.page, params.size, setParam]);

  // Contextual empty state ("No search data" vs "No data") when a query/facet is
  // on. Includes the Awardees first/last-name boxes (fn/ln).
  const isSearchActive = Boolean(
    params.q ||
      params.fn ||
      params.ln ||
      params.aau ||
      params.uta ||
      activeFacetCount > 0
  );

  // On phones the hero banner eats ~45% of the viewport; once the user is
  // actively searching/filtering, drop it so the results table gets the screen.
  const showHero = !(isMobile && isSearchActive);

  return (
    <div className="awards-results">
      {showHero && <AwardsHero />}

      <div className="awards-table-panel">
        <Grid
          columnDefs={isAwardees ? peopleColumnDefs : awardColumnDefs}
          rowData={isAwardees ? peopleRows : awardRows}
          serverSidePagination
          rowCount={totalRecords}
          serverSideSorting
          showPagination
          pageSizeList={[10, 25, 50, 100]}
          pageSize={params.size}
          page={params.page}
          masterDetail
          showTotalRowCount
          uniqueIdentifierFieldName={"id"}
          sortBy={onSort}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onRowClicked={isAwardees ? handlePersonClicked : handleAwardClicked}
          isLoading={isAwardees ? peopleLoading : awardsLoading}
          isSearch={isSearchActive}
        />
      </div>

      {isAwardees && (
        <Slider isOpen={openSlider} onClose={() => setOpenSlider(false)}>
          <Awardee
            awardeeData={awardeeData}
            sliderTitle={sliderTitle}
            utaUsername={sliderUsername}
            handleOnCloseClick={() => setOpenSlider(false)}
          />
        </Slider>
      )}
    </div>
  );
}
